import { History } from 'history';
import pick from 'lodash-es/pick';
import upperFirst from 'lodash-es/upperFirst';
import {
    COMMON_PLACES_ABROAD,
    FORUM_TOPICS_NOT_FOUND_query,
    FORUM_TOPICS_SEARCH_PLACEHOLDER,
    LOAD_PREVIOUS,
} from 'mk/autogenerated/translations/CommonPlaceTopics.4016c3137c9e3ed2719da8e2ac901e20'
import assetUrl from 'mk/common/assetUrl';
import { forumCommonPlaceForeignTopicsUrl, forumCommonPlaceTopicsUrl } from 'mk2/apps/forum/urls';
import { ImgCropped, ImgCropMode } from 'mk2/components/ImgCropped';
import { InfiniteListLoadable } from 'mk2/components/InfiniteListLoadable';
import { Link } from 'mk2/components/Link';
import { ListItem, ListItemPlaceholder } from 'mk2/components/ListItem';
import { ListItemIcon } from 'mk2/components/ListItemIcon';
import { NoResults } from 'mk2/components/NoResults';
import { SearchBox } from 'mk2/components/SearchBox';
import { LoadingSwitch } from 'mk2/containers/LoadingSwitch/LoadingSwitch';
import { url } from 'mk2/helpers/urls';
import { isSuccess, LoadingState } from 'mk2/reducers/loadingState';
import { interpolate } from 'mk2/services/i18n';
import { parse, stringify } from 'query-string';
import React from 'react';
import { Helmet } from 'react-helmet';
import { Route } from 'react-router';
import { ListRowProps } from 'react-virtualized';
import styles from './CommonPlaceTopics.mscss';
import { Props } from './CommonPlaceTopicsPage';

const ROW_HEIGHT = 44;

export class CommonPlaceTopics extends React.PureComponent<Props> {
    public UNSAFE_componentWillMount() {
        const { queryFromState, queryFromUrl, onUpdateSearch } = this.props;

        if (queryFromUrl && queryFromUrl !== queryFromState) {
            onUpdateSearch(queryFromUrl);
        }
    }

    public componentDidUpdate(prevProps: Props) {
        const { queryFromState, queryFromUrl, onUpdateSearch } = this.props;

        // After has changed
        if (this.props.after !== prevProps.after) {
            this.load();
        }

        // Update search if changed by URL
        if (queryFromUrl !== prevProps.queryFromUrl && queryFromUrl !== queryFromState) {
            onUpdateSearch(queryFromUrl);
        }
    }

    public componentWillUnmount() {
        const { onUpdateSearch } = this.props;
        onUpdateSearch('');
    }

    public render() {
        const { loadingState, items } = this.props;

        return (
            <div className={styles.ForumTopics}>
                <LoadingSwitch
                    loadingState={loadingState}
                    onLoad={this.load}
                    onRenderInit={this.renderInit}
                    onRenderSuccess={this.renderSuccess}
                    hasData={!!items && items.length > 0}
                />
            </div>
        );
    }

    private renderInit = () => {
        return (
            <>
                <ListItemPlaceholder icon isLast={false} />
                <ListItemPlaceholder icon isLast={false} />
                <ListItemPlaceholder icon isLast={false} />
                <ListItemPlaceholder icon isLast={false} />
                <ListItemPlaceholder icon isLast />
            </>
        );
    };

    private renderSuccess = () => {
        const {
            category, hasNext, hasPrevious, items, loadingState, location, placeType, queryFromState, queryFromUrl,
        } = this.props;

        // InfiniteScroll data
        // @see https://github.com/bvaughn/react-virtualized/blob/master/docs/creatingAnInfiniteLoadingList.md
        const topicsRowCount = hasNext ? items.length + 1 : items.length;
        const rowCount = (placeType === 'counties' && !queryFromUrl && !hasNext && isSuccess(loadingState))
            ? topicsRowCount + 1 : topicsRowCount;

        return (
            <>
                <Route exact path={forumCommonPlaceTopicsUrl} children={this.renderHeaders} />

                <SearchBox
                    placeholder={FORUM_TOPICS_SEARCH_PLACEHOLDER}
                    query={queryFromState}
                    onChange={this.onSearchChange}
                    autoFocus={false}
                />

                {hasPrevious && isSuccess(loadingState) && (
                    <div className={styles.ForumTopics__loadPrevious}>
                        <Link
                            className={styles.ForumTopics__loadPrevious__link}
                            to={{ ...location, search: `?${stringify({ ...parse(location.search), after: -items[0].id })}`}}
                            onClick={this.loadPrevious}
                        >
                            {LOAD_PREVIOUS}
                        </Link>
                    </div>
                )}

                {category && (
                    <div className={styles.ForumTopics__space}>
                        <h1 className={styles.ForumTopics__space__text}>
                            {category.name}
                        </h1>
                    </div>
                )}

                {items.length === 0 ? (
                    <NoResults message={interpolate(FORUM_TOPICS_NOT_FOUND_query, {query: queryFromUrl})} />
                ) : (
                    <InfiniteListLoadable
                        isRowLoaded={this.isRowLoaded}
                        loadMoreRows={this.loadNext}
                        rowCount={rowCount}
                        rowHeight={ROW_HEIGHT}
                        rowRenderer={(props: ListRowProps) => this.rowRenderer(props) /* tslint:disable-line */}
                    />
                )}
            </>
        );
    };

    private load = () => {
        // Do not trigger next load
        if (this.props.loadingState === LoadingState.LOADING) {
            return;
        }

        const { after, onLoad, placeType, queryFromUrl } = this.props;
        onLoad(placeType, queryFromUrl, after);
    };

    private loadNext = () => {
        // Do not trigger next load
        if (this.props.loadingState === LoadingState.LOADING) {
            return;
        }

        const { items, onLoad, placeType, queryFromUrl } = this.props;
        const after = items[items.length - 1].id;
        onLoad(placeType, queryFromUrl, after);

        return new Promise((resolve) => {
            setTimeout(resolve, 500);
        });
    };

    private loadPrevious = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        // Do not trigger next load
        if (this.props.loadingState === LoadingState.LOADING) {
            return;
        }

        const { items, onLoad, placeType, queryFromUrl } = this.props;
        const after = -items[0].id;
        onLoad(placeType, queryFromUrl, after);
    };

    private isRowLoaded = ({ index }) => !this.props.hasNext || index < this.props.items.length;

    private rowRenderer = ({ index, key, style }) => {
        const { countriesRowNo, hasNext, items, itemToTopicUrl } = this.props;

        if (index === countriesRowNo) {
            return (
                <ListItem
                    hasMore
                    icon={
                        <ImgCropped
                            imgUrl={`${assetUrl}img/forum/earth.png`}
                            imgWidth={64}
                            imgHeight={64}
                            width={32}
                            height={32}
                            className={styles.ListItemIcon}
                            mode={ImgCropMode.Cover}
                            alt="earth"
                        />
                    }
                    isLast
                    key={key}
                    style={style}
                    to={forumCommonPlaceForeignTopicsUrl}
                >
                    {COMMON_PLACES_ABROAD}
                </ListItem>
            );
        }

        if (!this.isRowLoaded({ index })) {
            return (
                <ListItemPlaceholder key={key} style={style} icon isLast />
            );
        }

        const item = items[index];

        return (
            <ListItem
                count={item.numThreads}
                icon={<ListItemIcon lazy photo={item.coverPhoto} />}
                isLast={!countriesRowNo && !hasNext && items.length === index + 1}
                key={key}
                style={style}
                to={itemToTopicUrl(item)}
            >
                {upperFirst(item.name)}
            </ListItem>
        );
    };

    private onSearchChange = (q: string) => {
        const { onSearch, placeType } = this.props;
        onSearch(placeType, q);
    };

    private renderHeaders = ({ match }) => {
        const { loadingState, hasNext, hasPrevious, baseUrl, items, location } = this.props;
        if (match && isSuccess(loadingState)) {
            const parsed = parse(location.search) as { search: string, after: string };
            const qs = pick<{ search: string, after: string }>(parsed, [ 'search', 'after' ]);

            return (
                <Helmet>
                    {hasNext && <link rel="next" href={baseUrl + url(forumCommonPlaceTopicsUrl, {}, {...qs, after: items[items.length - 1].id})} />}
                    {hasPrevious && <link rel="prev" href={baseUrl + url(forumCommonPlaceTopicsUrl, {}, { ...qs, after: -items[0].id })} />}
                </Helmet>
            );
        }

        return null;
    };
}
